import React from "react";
import { observer } from "mobx-react-lite";
import { Hooks, Context, Cookie } from "@xt/xtbase";
import { useRouter } from "next/router";
import LocalesMap from "locales/map";
import styles from "./index.module.scss";
import store from "@/store";

const CHEVRON_RIGHT = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.5 2.5L7.5 6L4.5 9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// 计价方式列表（与 store/currency 一致，用于展示）
const CURRENCY_LIST = [
  { key: "usd", name: "USD" },
  { key: "krw", name: "KRW" },
  { key: "inr", name: "INR" },
  { key: "idr", name: "IDR" },
  { key: "cny", name: "CNY" },
];

interface SettingProps extends BaseCompProps {
  attr?: any;
}

const { useTranslation } = Hooks;
const { XtContext } = Context;

// 将 Next 的 locale（如 zh-CN）转为 LocalesMap 的 key（如 cn）
const localeToLangKey = (locale: string | undefined): string | undefined =>
  !locale || locale === "default" ? undefined : locale === "zh-CN" ? "cn" : locale === "zh-HK" ? "hk" : locale;

// 从当前 pathname 第一段解析 locale（兜底：SSR/首屏时 router.locale 可能未就绪）
const getLangKeyFromPathname = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  const first = (window.location.pathname || "").split("/").filter(Boolean)[0];
  if (!first) return undefined;
  const found = LocalesMap.find((item: { key: string; name: string }) => item.key === first || item.name === first);
  return found ? (found as { key: string }).key : undefined;
};

const Setting: React.FC<SettingProps> = ({ className }) => {
  const t = useTranslation();
  const { push, locale: routerLocale } = useRouter();
  const [appState] = React.useContext(XtContext);
  const [pathnameLang, setPathnameLang] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    setPathnameLang(getLangKeyFromPathname());
  }, []);
  const lang =
    (typeof window !== "undefined" ? Cookie.get("lang") : null) ??
    appState?.lang ??
    localeToLangKey(routerLocale) ??
    pathnameLang ??
    "en";
  const currency = appState?.currency ?? (typeof window !== "undefined" ? Cookie.get("currency") : null) ?? "usd";

  const currentLangLab = LocalesMap.find((item: { key: string }) => item.key === lang)?.lab ?? lang;
  const currentCurrencyName = CURRENCY_LIST.find((c) => c.key === currency)?.name ?? "USD";

  const items = [
    { key: "language", label: t("setting.language"), value: currentLangLab, path: "/accounts/user/setting-language" },
    // { key: "currency", label: t("setting.currency"), value: currentCurrencyName, path: "/accounts/user/setting-currency" }, // 法币切换先注释
    // { key: "theme", label: t("setting.theme"), value: themeLabel, path: "", isTheme: true },
    { key: "notification", label: t("setting.notification"), value: "", path: "/accounts/user/notification-setting" },
  ];

  return (
    <div className={[styles["settings-page"], className].filter(Boolean).join(" ")}>
      <div className={styles["settings-list"]}>
        {items.map((item) => (
          <div
            key={item.key}
            role="button"
            tabIndex={0}
            className={styles["settings-list-item"]}
            onClick={() => item.path && push(item.path)}
            onKeyDown={(e) => e.key === "Enter" && item.path && push(item.path)}
          >
            <span className={styles["settings-list-label"]}>{item.label}</span>
            {item.value ? <span className={styles["settings-list-value"]}>{item.value}</span> : null}
            <span className={styles["settings-list-chevron"]}>{CHEVRON_RIGHT}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default observer(Setting);
